import BACKEND_URL from '../config/backendUrl';

const announcedKeys = new Set();
let announcementQueue = Promise.resolve();
const ANNOUNCEMENT_STORAGE_KEY = 'odoo-dashboard:spoken-announcements';
const ANNOUNCEMENT_LOCK_NAME = 'odoo-dashboard:announcement-claim';
const ANNOUNCEMENT_RETENTION_MS = 24 * 60 * 60 * 1000;
const PREFERRED_FRENCH_VOICES = [
  'Microsoft Vivienne',
  'Microsoft Denise',
  'Microsoft Eloise',
  'Microsoft Julie',
  'Microsoft Hortense',
  'Microsoft',
  'Audrey',
  'Amélie',
  'Amelie',
  'Aurélie',
  'Aurelie',
  'Marie',
  'Google français',
];

export const getPomodoroStartAnnouncement = (name, durationSeconds) => {
  const minutes = Math.max(1, Math.round((Number(durationSeconds) || 0) / 60));
  return `${name} a démarré une session Pomodoro de ${minutes} minute${minutes > 1 ? 's' : ''}.`;
};

export const getPomodoroEndAnnouncement = name => `${name} a terminé son Pomodoro.`;
export const getNewTicketAnnouncement = (clientName, ticketTitle) =>
  `Bonjour la super Team, un nouveau ticket est tombé, le client ${clientName} annonce que ${ticketTitle}.`;
export const getEventReminderAnnouncement = (organizerName, title, minutes) =>
  `${organizerName}, ta réunion ${title} démarre dans ${minutes} min.`;
export const getIntercomAnnouncement = (callerName, targetName) =>
  `${targetName} ! ${callerName} te demande.`;

export function selectPomodoroVoice(voices = []) {
  const frenchVoices = voices.filter(voice => voice.lang?.toLowerCase().startsWith('fr'));
  return [...frenchVoices].sort((voiceA, voiceB) => {
    const rank = voice => {
      const index = PREFERRED_FRENCH_VOICES.findIndex(name => voice.name?.toLowerCase().includes(name.toLowerCase()));
      return index === -1 ? PREFERRED_FRENCH_VOICES.length : index;
    };
    return rank(voiceA) - rank(voiceB);
  })[0];
}

function speakWithBrowserVoice(message, tone = 'neutral') {
  if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return false;
  const utterance = new window.SpeechSynthesisUtterance(message);
  utterance.lang = 'fr-FR';
  utterance.rate = tone === 'lively' ? 1.05 : 0.87;
  utterance.pitch = tone === 'lively' ? 1.12 : 1.08;
  utterance.volume = 1;
  const frenchVoice = selectPomodoroVoice(window.speechSynthesis.getVoices?.() || []);
  if (frenchVoice) utterance.voice = frenchVoice;
  window.speechSynthesis.speak(utterance);
  return true;
}

function readPersistedAnnouncements(now = Date.now()) {
  try {
    const stored = JSON.parse(window.localStorage?.getItem(ANNOUNCEMENT_STORAGE_KEY) || '{}');
    return Object.fromEntries(
      Object.entries(stored).filter(([, announcedAt]) => now - Number(announcedAt) < ANNOUNCEMENT_RETENTION_MS)
    );
  } catch (error) {
    return {};
  }
}

function persistAnnouncementClaim(key, now = Date.now()) {
  try {
    const announcements = readPersistedAnnouncements(now);
    if (announcements[key]) return false;
    announcements[key] = now;
    window.localStorage?.setItem(ANNOUNCEMENT_STORAGE_KEY, JSON.stringify(announcements));
    return true;
  } catch (error) {
    // La déduplication en mémoire reste active si le stockage du navigateur est indisponible.
    return true;
  }
}

async function claimAnnouncement(key) {
  if (window.navigator?.locks?.request) {
    try {
      return await window.navigator.locks.request(ANNOUNCEMENT_LOCK_NAME, () => persistAnnouncementClaim(key));
    } catch (error) {
      // Certains navigateurs exposent l'API sans l'autoriser dans tous les contextes.
    }
  }
  return persistAnnouncementClaim(key);
}

async function playMicrosoftVoice(message, tone = 'neutral') {
  if (typeof window.Audio === 'undefined' || !window.URL?.createObjectURL || !window.fetch) throw new Error('Lecture audio indisponible');
  const response = await window.fetch(`${BACKEND_URL}/api/speech/synthesize`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text: message, tone }),
  });
  if (!response.ok) throw new Error('Microsoft TTS indisponible');
  const audioBlob = await response.blob();
  const audioUrl = window.URL.createObjectURL(audioBlob);

  try {
    const audio = new window.Audio(audioUrl);
    await new Promise((resolve, reject) => {
      let playbackStarted = false;
      audio.addEventListener('ended', resolve, { once: true });
      audio.addEventListener('error', () => {
        // Si le son a déjà commencé, relancer toute la phrase avec une autre voix
        // produirait précisément une annonce en double.
        if (playbackStarted) resolve();
        else reject(new Error('Lecture Microsoft TTS impossible'));
      }, { once: true });
      const playback = audio.play();
      if (playback?.then) playback.then(() => { playbackStarted = true; }).catch(reject);
    });
  } finally {
    window.URL.revokeObjectURL(audioUrl);
  }
}

export function speakPomodoroAnnouncement(message, key, tone = 'neutral') {
  if (typeof window === 'undefined') return false;
  if (announcedKeys.has(key)) return false;
  announcedKeys.add(key);

  announcementQueue = announcementQueue.then(async () => {
    if (!await claimAnnouncement(key)) return false;
    try {
      await playMicrosoftVoice(message, tone);
      return true;
    } catch (error) {
      return speakWithBrowserVoice(message, tone);
    }
  });
  return true;
}

export function announcePomodoroStart(pomodoro, fallbackName = 'Un utilisateur') {
  const name = pomodoro.user_name || fallbackName;
  const key = `pomodoro-start-${pomodoro.user_id || name}-${pomodoro.started_at || pomodoro.ends_at}`;
  return speakPomodoroAnnouncement(getPomodoroStartAnnouncement(name, pomodoro.duration_seconds), key);
}

export function announcePomodoroEnd(pomodoro, fallbackName = 'Un utilisateur') {
  const name = pomodoro.user_name || fallbackName;
  const key = `pomodoro-end-${pomodoro.user_id || name}-${pomodoro.started_at || pomodoro.ends_at}`;
  return speakPomodoroAnnouncement(getPomodoroEndAnnouncement(name), key);
}

export function announceNewTicket(ticket) {
  return speakPomodoroAnnouncement(
    getNewTicketAnnouncement(ticket.client_name, ticket.title),
    `new-ticket-${ticket.id}`
  );
}

export function announceUpcomingEvent(event, now = new Date()) {
  const minutes = Math.max(1, Math.ceil((new Date(event.starts_at).getTime() - now.getTime()) / 60000));
  return speakPomodoroAnnouncement(
    getEventReminderAnnouncement(event.organizer_name, event.title, minutes),
    `event-reminder-${event.id}-${event.starts_at}`
  );
}

export function announceIntercomCall(call) {
  return speakPomodoroAnnouncement(
    getIntercomAnnouncement(call.caller_name, call.target_name),
    `intercom-call-${call.id}`,
    'lively'
  );
}
